/**
 * Pathless layout for pages that must render for anonymous visitors and
 * crawlers: studio, instructor and class detail, plus their public review
 * lists. It deliberately applies no auth gate, because these are the pages the
 * slugs exist for.
 *
 * Review form routes still live under this tree, so they are guarded
 * individually in beforeLoad. Authentication is also enforced by the API, so a
 * guard here only prevents an anonymous visitor reaching a dead-end form.
 */
import { createFileRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { EntityDetailTemplatePending } from '@/components/v2/page-template-pending';
import pageEaseInStyles from '@/routes/page-ease-in.module.less';

export const Route = createFileRoute('/_public')({
  component: PublicLayout,
  // Layout-crossing navigations (e.g. /discovery → /instructors/$id) pend the
  // LAYOUT match while its subtree's chunks load, and the pending UI shown is
  // the layout's pendingComponent — without this, the router falls back to the
  // full-page RoutePending mascot and the detail click reads as
  // "nothing happened, now we wait" (perf audit part 2, Theme 2).
  pendingComponent: EntityDetailTemplatePending,
});

function PublicLayout() {
  // App-wide page ease-in, mirroring the authed layout: keyed by pathname so
  // every navigation (including back) remounts and replays the fade, while
  // search-only changes keep the key. Opacity-only animation — see
  // page-ease-in.module.less for the position:fixed constraint.
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  return (
    <div key={pathname} className={pageEaseInStyles.pageEaseIn}>
      <Outlet />
    </div>
  );
}
